import { ReactNode, useEffect, useRef, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  COOKIES_CONFIG,
  INTERCOM_CONFIG,
  ImpressionTracker,
  PageViewTracker,
  template,
  US_STATES_CODES,
  PATHS,
  NEW_GUARANTEED_TIME_TO_LOVE_HOMEOWNERS_LANDING_PAGE_EXPERIMENT,
  HOMEOWNERS_HERO_COPY_V2_EXPERIMENT,
} from '@belong/common';
import {
  Text,
  TestimonialsCarousel,
  GoogleStars,
  Link,
  Button,
  BelongExplainerVideo,
} from '@belong/ui';
import { PrismicDocument } from '@prismicio/types';
import clsx from 'clsx';
import { parseCookies } from 'nookies';
import { trackEventService } from 'src/api/tracking';
import { type IntercomWidgetProps } from 'src/components/intercom-widget/intercom-widget';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { RefreshMiamiBanner } from 'src/components/refresh-miami-banner/refresh-miami-banner';
import { SfistBayAreaBanner } from 'src/components/sfist-bay-area-banner/sfist-bay-area-banner';
import { SanDiegoShortTermRentalsBanner } from 'src/features/homeowners/componentes/san-diego-short-term-rentals-banner/san-diego-short-term-rentals-banner';
import {
  GA_EVENTS,
  GA_ENTRY_POINTS,
  GA_EXPERIMENT,
} from 'src/features/rent-estimate/constants';
import { useUser } from 'src/features/users/useUser';
import { TownTourSection } from 'src/modals/how-it-works/town-tour/town-tour-section';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import { getExperimentVariantFromCookies } from 'src/utils/experiments';
import { NAVBAR_ACTIONS } from '../../constants';
import { HOMEOWNERS_STRINGS as STRINGS } from '../../homeowners.strings';
import { CoverageCity } from '../../homeowners.types';
import { useOnScreen } from '../../hooks/use-on-screen';
import { BlogCarousel } from '../blog-carousel/blog-carousel';
import { CalculatorSectionVariant } from '../calculator-section/calculator-section-variant';
import { CoverageMap } from '../coverage-map/coverage-map';
import { Footbar } from '../footbar/footbar';
import { GttlCover } from '../gttl-landing-page/components/gttl-cover/gttl-cover';
import { PriceTablePreview } from '../price-table-preview/price-table-preview';
import { ReferralMessage } from '../referral-message/referral-message';
import { StickyNavbar } from '../sticky-navbar/sticky-navbar';
import { GttlBelovedHome } from './components/gttl-beloved-home/gttl-beloved-home';
import { GttlGuaranteedRent } from './components/gttl-guaranteed-rent/gttl-guaranteed-rent';
import { GttlPhotoWall } from './components/gttl-photo-wall/gttl-photo-wall';
import { GttlResidentsLove } from './components/gttl-residents-love/gttl-residents-love';
import css from './gttl-landing-page.module.css';
import type { InitialZustandState } from 'src/store/region';

const IntercomWidget = dynamic<IntercomWidgetProps>(
  () =>
    import('src/components/intercom-widget/intercom-widget').then(
      (mod) => mod.IntercomWidget,
    ),
  { ssr: false },
);

export const GttlLandingPage: NextPage<ServerProps> & StaticMethods = ({
  blogPosts,
  coverageCities,
  heroCopyVariant,
}) => {
  const [isStickyBarVisible, setIsStickyBarVisible] = useState(false);
  const priceTableRef = useRef<HTMLDivElement>(null);
  const region = useStore(regionSelector);

  const ctaRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(ctaRef);
  const previousIsOnScreen = useRef(isOnScreen);

  const router = useRouter();
  const { user } = useUser();

  const cookies = parseCookies();

  const comeFromMobileApp = Boolean(
    cookies[COOKIES_CONFIG.MOBILE_APP.name] === 'true',
  );

  const AnimatedStickyNavbar = animated(StickyNavbar);
  const AnimatedFootbar = animated(Footbar);
  const transitions = useTransition(isStickyBarVisible, {
    from: { y: -100 },
    enter: { y: 0 },
    leave: { y: -100 },
  });

  useEffect(() => {
    if (isOnScreen && !previousIsOnScreen.current) {
      setIsStickyBarVisible(false);
    } else if (!isOnScreen && previousIsOnScreen.current) {
      setIsStickyBarVisible(true);
    }

    previousIsOnScreen.current = isOnScreen;
  }, [isOnScreen]);

  function scrollToPriceTable() {
    const priceTable = priceTableRef.current;
    if (priceTable) {
      window.scrollBy({
        top: priceTable.getBoundingClientRect().top,
        behavior: 'smooth',
      });
    }
  }

  let metaTitle = STRINGS['meta.title'];
  let metaDescription = STRINGS['meta.description'];

  if (region.id) {
    metaTitle = template(STRINGS['meta.title.withRegion'], {
      regionName: region.region,
    }) as unknown as string;

    metaDescription = template(STRINGS['meta.description.withRegion'], {
      regionName: region.region,
    }) as unknown as string;
  }

  const isFloridaRegion = region.state === US_STATES_CODES.FL;
  const isSanDiegoRegion = region?.name === 'SanDiego';
  const isBayArea = region?.name === 'BayArea';

  const handleRentEstimateClick = async () => {
    await trackEventService({
      eventName: GA_EVENTS.FLOW_STARTED,
      metadata: {
        ...(user?.userId && { userId: user.userId }),
        variant: GA_ENTRY_POINTS.HO_STICKY_NAV_BAR,
        experiment: GA_EXPERIMENT.RENT_ESTIMATE,
      },
    });

    router.push(PATHS.RENT_ESTIMATE_FLOW);
  };

  return (
    <PageViewTracker
      name="homeowners_landing_page"
      trackingData={{
        [NEW_GUARANTEED_TIME_TO_LOVE_HOMEOWNERS_LANDING_PAGE_EXPERIMENT.name]:
          getExperimentVariantFromCookies(
            NEW_GUARANTEED_TIME_TO_LOVE_HOMEOWNERS_LANDING_PAGE_EXPERIMENT.name,
          ),
        [HOMEOWNERS_HERO_COPY_V2_EXPERIMENT.name]: heroCopyVariant,
      }}
    >
      <IntercomWidget
        isEnabled={INTERCOM_CONFIG.homeowner.isEnabled}
        isStickyBarVisible={isStickyBarVisible}
      />

      <MetaSEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
        }}
      />

      <div className={clsx('w-full')}>
        <div
          className={clsx(
            css.heroLight,
            'bg-dark-navy relative overflow-hidden',
          )}
        >
          <div className={css.heroLightGradient} />
          <div className="z-10">
            <div className="h-xs container relative flex justify-end">
              <Text
                as="h1"
                variant="p2"
                className="bottom-xs text-dark-gray absolute"
              >
                {!region?.name || region?.name === 'Nationally'
                  ? 'Property Management With Guaranteed Rent'
                  : `${region?.region} Property Management`}
              </Text>
            </div>
            <GttlCover ctaRef={ctaRef} heroCopyVariant={heroCopyVariant} />
          </div>
        </div>
        <div className="w-full" id="presentation-video">
          <BelongExplainerVideo
            autoplay={!comeFromMobileApp}
            wrapperClassName={css.video_container}
            playIconStyle="green"
            videoClassName="border-none"
          />
        </div>
        <div className="relative">
          <GttlGuaranteedRent />

          <GttlResidentsLove />

          <GttlBelovedHome />

          {isFloridaRegion && (
            <article className="mb-5xl mt-2xl px-2sm container">
              <RefreshMiamiBanner />
            </article>
          )}
          {isBayArea && (
            <article className="mb-5xl mt-2xl px-2sm container">
              <SfistBayAreaBanner />
            </article>
          )}
          {isSanDiegoRegion && (
            <article className="mb-5xl mt-2xl px-2sm">
              <SanDiegoShortTermRentalsBanner />
            </article>
          )}

          <div id="pricing-table" className="mb-2xl mt-5xl container">
            <PriceTablePreview
              title={STRINGS['priceTable.title']}
              subtitle={STRINGS['priceTable.subtitle']}
              regionId={region.id}
              showMore={STRINGS['priceTable.showMore']}
              showLess={STRINGS['priceTable.showLess']}
              priceTableRef={priceTableRef}
              enableToggleExpansion={false}
            />
          </div>
          <div className="mb-5xl md:mb-6xl container">
            <CalculatorSectionVariant open />
          </div>
          <div className="mb-5xl md:mb-6xl w-full">
            <TownTourSection
              intro={
                <>
                  <Text
                    variant="h2"
                    fontWeight="semibold"
                    className={clsx(
                      css.stepsSection2Title,
                      'mb-xs lg:max-w-none',
                    )}
                  >
                    {STRINGS['how-it-works.title']}
                  </Text>
                  <Text variant="body">{STRINGS['how-it-works.subtitle']}</Text>
                </>
              }
            />
          </div>
          <ImpressionTracker section="testimonial_carousel">
            <div className="bg-darker-navy py-5xl flex w-full flex-col items-center">
              <div className="mb-xl md:mb-2xl flex flex-col items-center">
                <Text
                  variant="h2"
                  fontWeight="semibold"
                  className={clsx(
                    'pb-sm text-center text-white',
                    css.testimonialTitleContainer,
                  )}
                >
                  {STRINGS['testimonialsSection.title']}
                </Text>
                <GoogleStars theme="dark" />
              </div>
              <div className="carousel-container">
                <TestimonialsCarousel
                  theme="dark"
                  blurredEnds={false}
                  playIconStyle="green"
                />
              </div>
            </div>
          </ImpressionTracker>

          <GttlPhotoWall />

          {blogPosts?.length > 0 && (
            <div className="mb-5xl mt-2xl bg-darker-navy py-5xl md:mb-6xl flex w-full flex-col items-center">
              <Text
                variant="h2"
                fontWeight="semibold"
                className="mb-lg md:mb-xl text-white"
              >
                {STRINGS['blogSection.title']}
              </Text>
              <div className={clsx('carousel-container mb-lg md:mb-xl')}>
                <BlogCarousel
                  blogPosts={blogPosts}
                  theme="dark"
                  blurredEnds={false}
                />
              </div>
              <Link
                href="/blog"
                variant="solid"
                buttonType="primary"
                size="large"
              >
                {STRINGS['blogSection.ctaLabel']}
              </Link>
            </div>
          )}
          <div className="mb-3xl md:mb-4xl container flex flex-col  items-center justify-center">
            <div className="mb-2xl">
              <Text variant="h2" fontWeight="semibold">
                {STRINGS['map.title']}
              </Text>
            </div>
            <CoverageMap coverageCities={coverageCities} />
          </div>
          {transitions(
            (styles, isStickyBarVisible) =>
              isStickyBarVisible && (
                <>
                  <AnimatedStickyNavbar
                    style={styles}
                    handlePricingClick={scrollToPriceTable}
                  />
                  <AnimatedFootbar
                    style={{ y: styles.y.to((y) => -y) }}
                    buttonLeft={
                      <>
                        <div className="contents md:hidden">
                          {NAVBAR_ACTIONS.REGION_PHONE(region)}
                        </div>
                        <div className="hidden md:contents">
                          <Button
                            variant="text"
                            buttonType="secondary"
                            size="auto"
                            onClick={handleRentEstimateClick}
                          >
                            <span className="text-p1 leading-p1">{`Get\u00a0Rent\u00a0Estimate`}</span>
                          </Button>
                        </div>
                      </>
                    }
                    buttonRight={NAVBAR_ACTIONS.SEE_QUALIFY()}
                  />
                </>
              ),
          )}
        </div>
      </div>
      <ReferralMessage />
    </PageViewTracker>
  );
};

type StaticMethods = {
  getLayout?: (page: ReactNode, pageProps: ServerProps) => ReactNode;
};

type ServerProps = {
  blogPosts: PrismicDocument[];
  initialZustandState?: InitialZustandState;
  coverageCities: CoverageCity[];
  heroCopyVariant?: string;
};
